import { ReqType } from "@hoppscotch/common/helpers/backend/graphql"
import { CollectionDataProps } from "@hoppscotch/common/helpers/backend/helpers"
import { HoppCollection } from "@hoppscotch/data"
import * as E from "fp-ts/Either"
import { def } from "../web"
import { importUserCollectionsFromJSON } from "./api"
import { platform } from "@hoppscotch/common/platform"
import { environmentsStore } from "@hoppscotch/common/newstore/environments"
import { resolveImportedEnvironmentIDs } from "@app/lib/environment-import-links"

/**
 * Platform-specific import function for selfhost-web that uses the correct nested collection queries
 */
export const importToPersonalWorkspace = async (
  collections: HoppCollection[],
  reqType: ReqType
) => {
  try {
    const userID = platform.auth.getCurrentUser()?.uid
    if (!userID) return E.left("import_failed")
    const resolveCollection = async (
      collection: HoppCollection
    ): Promise<HoppCollection> => ({
      ...collection,
      ...(collection.environmentIds?.length
        ? {
            environmentIds: await resolveImportedEnvironmentIDs(
              userID,
              collection.environmentIds,
              () => environmentsStore.value.environments.map((env) => env.id)
            ),
          }
        : {}),
      folders: await Promise.all(collection.folders.map(resolveCollection)),
    })
    const resolved = await Promise.all(collections.map(resolveCollection))
    if (platform.auth.getCurrentUser()?.uid !== userID)
      return E.left("import_failed")
    const transformedCollection = resolved.map(
      translateToPersonalCollectionFormat
    )

    const res = await importUserCollectionsFromJSON(
      JSON.stringify(transformedCollection),
      reqType
    )

    if (E.isLeft(res)) return E.left("import_failed")
  } catch {
    // A failed/ambiguous write must not trigger another import via store sync.
    return E.left("import_failed")
  }
  try {
    // The mutation may return an ID, not a JSON export. Read the authoritative
    // trees with server IDs without appending entries that would sync again.
    await def.loadUserCollections!(reqType === ReqType.Rest ? "REST" : "GQL")
  } catch {
    // The import already committed. Reload can recover a failed read; retrying
    // the write here would create duplicate server collections.
    console.warn(
      "Collections were imported but could not be refreshed. Reload to load them."
    )
  }
  return E.right({ success: true })
}

export function translateToPersonalCollectionFormat(x: HoppCollection) {
  const folders: HoppCollection[] = (x.folders ?? []).map(
    translateToPersonalCollectionFormat
  )

  const data: CollectionDataProps = {
    auth: x.auth,
    headers: x.headers,
    variables: x.variables,
    description: x.description,
    environmentIds: x.environmentIds,
  }

  const obj = {
    ...x,
    folders,
    data: { ...data, _ref_id: x._ref_id },
  }

  return obj
}
